import moment from 'moment'
import {
  ILocation,
  IBand,
  IPickupTimePreference,
  IRateAPI,
  IRateType,
  ICarrierCapacity,
} from 'apps/marketplace/store/contractLanes/types'
import { areLimitsValid, timeRangeValid } from 'apps/marketplace/utils/helpers/rates'
import { validatePhone, validateEmail } from 'utils/validators'
import {
  SAME,
  DIFFERENT,
  RADIUS_RATE,
  LANE_RATE,
  CUSTOM_START_DATE,
  CUSTOM_END_DATE,
  LANE_ALERTS_TYPE,
} from '../constants/rates'

export const validateRate = ({
  actionType,
  radiusRates,
  rate,
  rateType,
}: {
  rateType?: IRateType
  rate: string
  radiusRates?: IBand[]
  actionType: string
}) => {
  // rate is optional for lane alerts
  if (actionType === LANE_ALERTS_TYPE) {
    return true
  }
  if (rateType === LANE_RATE) {
    const parsed = parseFloat(rate || '')
    return !Number.isNaN(parsed)
  }
  if (radiusRates) {
    const checkedRates = radiusRates.filter(({ checked }) => checked)
    return (
      checkedRates.length &&
      checkedRates.every(({ value }) => {
        const parsed = parseFloat(value || '')
        return !Number.isNaN(parsed)
      })
    )
  }
  return false
}

const validateLocations = ({
  deliveryLocation,
  pickupLocation,
  rateType,
}: {
  pickupLocation: ILocation
  deliveryLocation: ILocation
  rateType: IRateType | undefined
}) =>
  pickupLocation &&
  pickupLocation.state &&
  (rateType === RADIUS_RATE || (deliveryLocation && deliveryLocation.state))

const validateLimits = ({ carrierCapacity }: { carrierCapacity?: ICarrierCapacity }) => {
  const { loading, values } = carrierCapacity || { loading: false }
  return !loading && areLimitsValid(values)
}

const validateUser = ({
  dispatcher,
  user,
}: {
  dispatcher: number
  user?: {
    userType: string
    role: string
    firstName: string
    lastName: string
    email: string
    phone: string
    cellPhone: string
  }
}) => {
  if (dispatcher) {
    return true
  }

  const { cellPhone, email, firstName, lastName, phone, role, userType } = user || {}
  const cellPhoneCheck =
    userType === 'owner_operator' && cellPhone ? validatePhone(cellPhone) : true
  return (
    userType &&
    role &&
    firstName &&
    lastName &&
    validateEmail(email || '') &&
    validatePhone(phone || '') &&
    cellPhoneCheck
  )
}

const validatePickupTimePreferences = ({
  pickupTimeEveryday,
  pickupTimePreferences,
  pickupTimeType,
}: {
  pickupTimePreferences: IPickupTimePreference[]
  pickupTimeEveryday?: {
    startTime?: string | null
    endTime?: string | null
  }
  pickupTimeType: string
}) => {
  if (pickupTimeType === SAME) {
    return pickupTimeEveryday && timeRangeValid(pickupTimeEveryday)
  }
  if (pickupTimeType === DIFFERENT) {
    return pickupTimePreferences.every(timeRangeValid)
  }
  return true
}

const validateStartDate = ({
  startDate,
  startType,
}: {
  startType: string
  startDate: string | null
}) => startType !== CUSTOM_START_DATE || startDate

export const validateExpirationDate = ({
  expirationDate,
  expirationType,
}: {
  expirationType: string
  expirationDate: string | null
}) => {
  // return expirationType && (expirationType !== CUSTOM_END_DATE || expirationDate)
  if (expirationType === CUSTOM_END_DATE) {
    if (expirationDate) {
      const today = moment().startOf('day')
      const expDate = moment(expirationDate)
      return expDate > today
    }
    return false
  }
  return expirationType
}

const validateActionType = ({ actionType }: { actionType: string }) => !!actionType

const predicates = [
  validateRate,
  validateStartDate,
  validateExpirationDate,
  validateLocations,
  validateLimits,
  validateUser,
  validatePickupTimePreferences,
  validateActionType,
]

export const isRecurringRateValid = (recurringRate?: IRateAPI) => {
  if (recurringRate) {
    const { buttonClicked, carrierId } = recurringRate
    const flags = [carrierId, !buttonClicked]
    return flags.every(x => x) && predicates.every(predicate => predicate(recurringRate))
  }
  return false
}
